import { Schema } from "src/api/flowTypes";
import { WorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import {
  JsonRefPath,
  isJsonRefPath,
  isReferencingEntity,
  isReferencingEvent,
} from "src/schema/jsonRefUtils";
import { CaseSchemaWithMetadata } from "src/schema/schemaEditor/CaseSchemaEditor";
import { SchemaViewType } from "src/schema/schemaEditor/SchemaToggles";
import { PropertyUI } from "src/schema/schemaMappingUtils";

const isSchemaWithMetadata = (
  caseSchema?: Schema,
): caseSchema is CaseSchemaWithMetadata => {
  return (
    !!caseSchema &&
    "_template_metadata" in caseSchema &&
    caseSchema._template_metadata !== undefined
  );
};

export const isCaseSchema = (schema?: Schema): boolean => {
  return (
    !!schema &&
    !!schema?.properties?.case &&
    isSchemaWithMetadata(schema.properties.case as Schema)
  );
};

export const schemaContainsFeatures = (properties: PropertyUI[]) => {
  return properties.some(
    (property) =>
      property.fieldName === "features" && property.type[0] === "object",
  );
};

export const schemaContainsEvent = (properties: PropertyUI[]) => {
  return properties.some(
    (property) =>
      isJsonRefPath(property.type[0]) &&
      isReferencingEvent(property.type[0] as JsonRefPath),
  );
};

export const schemaContainsEntity = (properties: PropertyUI[]) => {
  return properties.some(
    (property) =>
      isJsonRefPath(property.type[0]) &&
      isReferencingEntity(property.type[0] as JsonRefPath),
  );
};

export const schemaContainsCase = (
  properties: PropertyUI[],
  schema: Schema,
) => {
  return (
    properties.some((property) => property.fieldName === "case") ||
    isCaseSchema(schema)
  );
};

export const FEATURES_PROPERTY: PropertyUI = {
  fieldName: "features",
  required: false,
  sensitive: false,
  type: ["object", "null"],
};

export const areSchemasEnabled = (
  properties: PropertyUI[],
  isOutputSchemaConfigured: boolean,
  schema: Schema,
) => {
  const hasNoProperties = properties.length === 0;
  const hasOneProperty = properties.length === 1;
  const hasTwoProperties = properties.length === 2;

  const containsEvent = schemaContainsEvent(properties);
  const containsEntity = schemaContainsEntity(properties);
  const containsCase = schemaContainsCase(properties, schema);
  const containsFeatures = schemaContainsFeatures(properties);

  const eventSchemaEnabled =
    !containsCase &&
    (hasNoProperties ||
      (hasOneProperty && (containsEvent || containsFeatures)) ||
      (hasTwoProperties && containsEvent && containsFeatures));

  const entitySchemaEnabled =
    !containsCase &&
    (hasNoProperties ||
      (hasOneProperty && (containsEntity || containsFeatures)) ||
      (hasTwoProperties && containsEntity && containsFeatures));

  const customSchemaEnabled =
    !containsCase ||
    isOutputSchemaConfigured ||
    (containsCase && properties.length > 1);

  const caseSchemaEnabled =
    (hasNoProperties || containsCase) && !isOutputSchemaConfigured;

  return {
    eventSchemaEnabled,
    entitySchemaEnabled,
    customSchemaEnabled,
    caseSchemaEnabled,
  };
};

export const detectSchema = (
  properties: PropertyUI[],
  isOutputSchemaConfigured: boolean,
  featureGates: WorkspaceFeatureGates,
  casesFeaturesFlag: boolean,
  schema: Schema,
) => {
  const { eventSchemaEnabled, entitySchemaEnabled, caseSchemaEnabled } =
    areSchemasEnabled(properties, isOutputSchemaConfigured, schema);

  if (properties.length === 0) {
    return SchemaViewType.CUSTOM;
  }

  if (eventSchemaEnabled && featureGates.featuresEventsEnabled) {
    return SchemaViewType.EVENT;
  }

  if (entitySchemaEnabled && featureGates.entitiesEnabled) {
    return SchemaViewType.ENTITY;
  }

  if (caseSchemaEnabled && casesFeaturesFlag) {
    return SchemaViewType.CASE;
  }

  return SchemaViewType.CUSTOM;
};
